import { useEffect, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Cookie, X, Settings2 } from "lucide-react";
import {
  ALL_ACCEPTED,
  DEFAULT_DENIED,
  getConsent,
  setConsent,
  type ConsentCategories,
} from "@/lib/cookieConsent";
import { detectLangFromPath } from "@/lib/site";

const COOKIES_PATH: Record<string, string> = {
  fr: "/cookies",
  en: "/en/cookies",
  nl: "/nl/cookies",
};

export function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = detectLangFromPath(location.pathname);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [draft, setDraft] = useState<ConsentCategories>(DEFAULT_DENIED);

  useEffect(() => {
    setMounted(true);
    const existing = getConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setDraft(existing.categories);
    }

    // Allow re-opening from elsewhere (e.g. footer link).
    const handler = () => {
      const current = getConsent();
      setDraft(current?.categories ?? DEFAULT_DENIED);
      setShowPrefs(true);
      setOpen(true);
    };
    window.addEventListener("2am:open-cookie-prefs", handler);
    return () => window.removeEventListener("2am:open-cookie-prefs", handler);
  }, []);

  if (!mounted || !open) return null;

  const acceptAll = () => {
    setConsent(ALL_ACCEPTED);
    setOpen(false);
    setShowPrefs(false);
  };
  const rejectAll = () => {
    setConsent(DEFAULT_DENIED);
    setOpen(false);
    setShowPrefs(false);
  };
  const savePrefs = () => {
    setConsent(draft);
    setOpen(false);
    setShowPrefs(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookies.banner.title")}
      className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-2 pb-2 sm:px-4 sm:pb-4"
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-[var(--border-strong)] bg-background/95 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-background/85">
        {!showPrefs ? (
          <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
            <div className="flex items-start gap-2.5 sm:flex-1">
              <div className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full border border-[var(--border-strong)] bg-background">
                <Cookie size={13} aria-hidden />
              </div>
              <p className="text-xs leading-relaxed text-foreground/75">
                <span className="font-medium text-foreground">{t("cookies.banner.title")}</span>{" "}
                {t("cookies.banner.body")}{" "}
                <Link
                  to={COOKIES_PATH[lang]}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {t("cookies.banner.learnMore")}
                </Link>
                .
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-1.5 sm:shrink-0">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                aria-label={t("cookies.banner.customize")}
                className="inline-flex items-center justify-center gap-1 rounded-full border border-[var(--border-strong)] px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-foreground/75 transition hover:border-foreground hover:text-foreground"
              >
                <Settings2 size={11} />
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="inline-flex items-center justify-center rounded-full border border-[var(--border-strong)] px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-foreground/75 transition hover:border-foreground hover:text-foreground"
              >
                {t("cookies.banner.rejectAll")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="btn-ink inline-flex items-center justify-center rounded-full px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.1em]"
              >
                {t("cookies.banner.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-base font-semibold tracking-tight text-foreground">
                  {t("cookies.prefs.title")}
                </h2>
                <p className="mt-1 text-sm text-foreground/65">
                  {t("cookies.prefs.subtitle")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                aria-label={t("cookies.prefs.close")}
                className="grid size-8 place-items-center rounded-full text-foreground/60 hover:bg-foreground/5 hover:text-foreground"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3">
              <CategoryRow
                title={t("cookies.cats.necessary.title")}
                desc={t("cookies.cats.necessary.desc")}
                checked
                disabled
                onChange={() => {}}
              />
              <CategoryRow
                title={t("cookies.cats.analytics.title")}
                desc={t("cookies.cats.analytics.desc")}
                checked={draft.analytics}
                onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
              />
              <CategoryRow
                title={t("cookies.cats.marketing.title")}
                desc={t("cookies.cats.marketing.desc")}
                checked={draft.marketing}
                onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
              />
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={rejectAll}
                className="inline-flex items-center justify-center rounded-full border border-[var(--border-strong)] px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-foreground/80 transition hover:border-foreground hover:text-foreground"
              >
                {t("cookies.banner.rejectAll")}
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="btn-ink inline-flex items-center justify-center rounded-full px-5 py-2 text-xs font-medium uppercase tracking-[0.12em]"
              >
                {t("cookies.prefs.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  desc,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start justify-between gap-4 rounded-xl border border-[var(--border)] bg-background/60 p-4 ${
        disabled ? "opacity-80" : "cursor-pointer hover:border-[var(--border-strong)]"
      }`}
    >
      <div className="flex-1">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="mt-1 text-xs leading-relaxed text-foreground/65">{desc}</div>
      </div>
      <span className="relative inline-flex shrink-0 items-center">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="h-5 w-9 rounded-full bg-foreground/15 transition peer-checked:bg-foreground" />
        <span className="pointer-events-none absolute left-0.5 top-0.5 size-4 rounded-full bg-background transition peer-checked:translate-x-4" />
      </span>
    </label>
  );
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("2am:open-cookie-prefs"));
}
